<template>
  <b-overlay :show="loading" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>

    <transition name="bounce">
      <b-container class="pt-5 mt-5 mb-5 custom-container container">
        <h4 class="font-weight-bold mt-4 mb-4 align-middle">Timesheets</h4>
        <b-card
          border-variant="light"
          bg-variant="light"
          body-class="px-0 py-0"
        >
          <b-tabs
            v-model="tabIndex"
            lazy
            nav-class="justify-content-center timesheet-tabs"
            active-nav-item-class="font-weight-bolder text-primary active"
          >
            <b-tab
              v-if="isBusinessAdmin || isSupervisorTradie"
              title="Dashboard"
            >
              <timesheet-dashboard-tab
                :active="tabIndex === 0"
                @loading="setLoading"
              />
            </b-tab>
            <b-tab v-if="isBusinessAdmin" title="Plant & Equipment">
              <plant-equipment-tab @loading="setLoading" />
            </b-tab>
            <b-tab v-if="!isSupervisorTradie" title="Timesheets">
              <timesheet-list-tab @loading="setLoading" />
            </b-tab>
            <b-tab v-if="isBusinessAdmin" title="Supervisor Timesheets">
              <supervisor-timesheet-tab :active="tabIndex === 3" />
            </b-tab>
            <b-tab v-if="isSupervisorTradie" title="My Submissions">
              <supervisor-submit-tab />
            </b-tab>
            <b-tab v-if="isSupervisorTradie" title="Crew History">
              <crew-history-tab />
            </b-tab>
            <b-tab v-if="isSupervisorTradie" title="Team Timesheets">
              <team-timesheet-tab />
            </b-tab>
            <b-tab v-if="!isSupervisorTradie || isBusinessAdmin" title="Map">
              <timesheet-map-tab @loading="setLoading" />
            </b-tab>
          </b-tabs>
        </b-card>
      </b-container>
    </transition>
  </b-overlay>
</template>

<script>
import TimesheetMapTab from '@/components/timesheets/TimesheetMapTab'
import TimesheetListTab from '@/components/timesheets/TimesheetListTab'
import TimesheetDashboardTab from '@/components/timesheets/TimesheetDashboardTab'
import SupervisorTimesheetTab from '@/components/timesheets/SupervisorTimesheetTab'
import SupervisorSubmitTab from '@/components/timesheets/SupervisorSubmitTab'
import CrewHistoryTab from '@/components/timesheets/CrewHistoryTab'
import TeamTimesheetTab from '@/components/timesheets/TeamTimesheetTab'
import PlantEquipmentTab from '@/components/timesheets/PlantEquipmentTab'
import { mapGetters } from 'vuex'

export default {
  components: {
    TimesheetMapTab,
    TimesheetListTab,
    TimesheetDashboardTab,
    SupervisorTimesheetTab,
    SupervisorSubmitTab,
    CrewHistoryTab,
    TeamTimesheetTab,
    PlantEquipmentTab,
  },

  data() {
    return {
      loading: false,
      // Deep-linked notifications (e.g. "Supervisor Timesheet Approved")
      // land here via ?tab=. b-tabs picks its initial active tab from this
      // value at mount, then resets to 0 once its child <b-tab>s finish
      // registering (isBusinessAdmin/isSupervisorTradie gate which tabs
      // exist) — a mounted()-hook correction loses that race. Computing the
      // right index up front, before b-tabs ever mounts, avoids it entirely.
      tabIndex: this._tabIndexForQuery(this.$route.query),
    }
  },

  computed: {
    ...mapGetters('auth', ['user', 'isProjectScoped']),

    isBusinessAdmin() {
      return !!(this.user && this.user.user_type === 'business_admin')
    },

    isSupervisorTradie() {
      return !!(
        this.user &&
        this.user.user_type === 'tradie' &&
        this.user.is_supervisor
      )
    },
  },

  watch: {
    '$route.query'(query) {
      const index = this._tabIndexForQuery(query)
      if (index !== undefined) this.tabIndex = index
    },
  },

  methods: {
    setLoading(loading) {
      this.loading = loading
    },

    // Also called from data() — see the tabIndex comment there for why this
    // can't just live in mounted(). Reads the user straight from the store
    // getter rather than the isBusinessAdmin/isSupervisorTradie computeds,
    // since computeds aren't set up yet when data() calls this.
    _tabIndexForQuery(query) {
      const user = this.$store.getters['auth/user']
      const isBusinessAdmin = !!(user && user.user_type === 'business_admin')

      if (query.tab === 'supervisor' && isBusinessAdmin) {
        // BA tab order: Dashboard(0), Plant & Equipment(1), Timesheets(2),
        // Supervisor Timesheets(3), Map(4)
        return 3
      } else if (query.tab === 'my-submissions' && !isBusinessAdmin) {
        // Supervisor tradie tab order: Dashboard(0), My Submissions(1), Crew History(2)
        return 1
      } else if (query.tab === 'crew-history' && !isBusinessAdmin) {
        return 2
      }
      return undefined
    },
  },
}
</script>

<style lang="scss" scoped>
/deep/ .timesheet-tabs {
  a {
    padding: 24px 16px 0 !important;
    margin-bottom: 0 !important;
  }
}

/* Small screens: render the tabs as distinct segmented pills (light grey when
 * inactive, filled blue when active) so they're easy to tell apart instead of
 * looking like plain blue links that blend together. */
@media (max-width: 767.98px) {
  /deep/ .timesheet-tabs {
    border-bottom: none;
    gap: 0.4rem;
    /* With 3+ tabs (e.g. Dashboard / My Submissions / Crew History for
       supervisor tradies) the pills wrap onto a second row. Without bottom
       padding here, that wrapped row sits flush against the white tab
       content pane below — the active pill's blue fill visually overlaps
       it. Give the nav room to breathe before the content starts. */
    padding: 0 0.5rem 0.6rem;

    a,
    .nav-link {
      padding: 0.4rem 0.7rem !important;
      margin-bottom: 0 !important;
      border: 1px solid #d5dbe2;
      border-radius: 8px;
      background-color: #f1f4f8;
      color: #3a4652 !important;
      font-weight: 600;
      font-size: 0.85rem;
      line-height: 1.2;
      text-align: center;
      white-space: nowrap;

      &.active {
        background-color: #006cff !important;
        border-color: #006cff;
        color: #fff !important;
      }
    }
  }
}
</style>
